/**
 * Symbol image client — fetches symbol SVGs through an injected resource
 * adapter (see the renderer contract in ../README.md).
 *
 * Used by the Live Tap "Tapestry" view to render generative compositions of
 * symbols + textpaths driven by meeting deriv data.
 *
 * `resources.loadSymbolImages(symbolId)` returns an array of image documents per
 * symbol; we pick the one with selected:true (or the first). The adapter is
 * responsible for any service-specific casing normalization before the docs
 * reach this client.
 *
 * SVGs contain inner textpath slot elements (id starts with "textpath", optionally
 * tagged with toptext / lines:N / color:#HEX). We don't strip them — the renderer
 * fills them with short phrases the same way tapgen lo_slots does.
 */

const CACHE_LIMIT = 64;

/**
 * @typedef {Object} SymbolImage
 * @property {string} symbolId          the symbol_id it was fetched by
 * @property {string} svg               raw SVG string from the resource adapter
 * @property {number} heightRatio       h/w ratio (>1 means taller than wide)
 * @property {Array<string>} styles
 */

/**
 * @typedef {Object} SymbolResources
 * @property {(symbolId: string) => Promise<Array>} loadSymbolImages
 */

/**
 * Build a memoizing symbol-image client bound to a resource adapter. Each
 * client owns its own LRU cache so distinct adapters (e.g. a fixture adapter
 * in tests) never share memoized results.
 *
 * @param {SymbolResources} resources
 */
export function createSymbolImageClient(resources) {
  const cache = new Map(); // symbol_id[#styles] -> Promise<SymbolImage | null>

  function lru(key, value) {
    if (cache.has(key)) cache.delete(key);
    cache.set(key, value);
    while (cache.size > CACHE_LIMIT) {
      const oldest = cache.keys().next().value;
      cache.delete(oldest);
    }
  }

  // "container" artwork is a vessel/frame style, not meant to fill a generic
  // tapestry slot — tapgen excludes it from in-tapestry use unless explicitly
  // requested (ttback restricts the unstyled case to no style or "classic").
  // Live tapestries are tapestries too, so mirror that: container artwork is
  // only eligible when a slot specifically asks for it by style.
  const isContainer = (d) =>
    Array.isArray(d?.styles) && d.styles.some((s) => String(s).toLowerCase() === 'container');

  // Pick the image doc: prefer one whose styles include a requested style
  // (e.g. the slot's "minimal" — including "container" when explicitly asked),
  // else the selected one, else the first. The fallback excludes container so it
  // never lands in a slot that didn't request it.
  function pickDoc(docs, styles) {
    if (Array.isArray(styles) && styles.length) {
      const want = new Set(styles.map((s) => String(s).toLowerCase()));
      const match = docs.find(
        (d) => Array.isArray(d.styles) && d.styles.some((s) => want.has(String(s).toLowerCase()))
      );
      if (match) return match;
    }
    const pool = docs.filter((d) => !isContainer(d));
    return pool.find((d) => d.selected) || pool[0] || null;
  }

  /**
   * Fetch a symbol image by symbol_id, optionally preferring image styles
   * (e.g. a curated slot's symbol_styles). Returns null if not found.
   * Memoized per (symbol_id, styles) so curated and generative requests
   * don't collide.
   */
  function fetchSymbolImage(symbolId, styles = null) {
    if (!symbolId) return Promise.resolve(null);
    const cacheKey =
      styles && styles.length
        ? `${symbolId}#${[...styles]
            .map((s) => String(s).toLowerCase())
            .sort()
            .join(',')}`
        : symbolId;
    if (cache.has(cacheKey)) return cache.get(cacheKey);

    const promise = (async () => {
      try {
        const docs = await resources.loadSymbolImages(symbolId);
        if (!Array.isArray(docs) || docs.length === 0) return null;
        const picked = pickDoc(docs, styles);
        if (!picked?.image_file) return null;
        return {
          symbolId,
          svg: picked.image_file,
          heightRatio: picked.height_ratio || 1,
          styles: picked.styles || [],
        };
      } catch (err) {
        console.error(`[symbolImageClient] fetch failed for "${symbolId}":`, err);
        return null;
      }
    })();

    lru(cacheKey, promise);
    return promise;
  }

  /** Fetch many symbols in parallel. Order preserved; nulls included for misses. */
  function fetchSymbolImages(symbolIds) {
    return Promise.all((symbolIds || []).map((symbolId) => fetchSymbolImage(symbolId)));
  }

  /** For tests / dev tools. */
  function clearCache() {
    cache.clear();
  }

  return { fetchSymbolImage, fetchSymbolImages, clearCache };
}
